import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus, Scale, Trash2, Trophy } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";

const newQuote = () => ({ supplier_id: "", rate: "", gst_pct: 18, transport: 0, other: 0, delivery_days: 0, terms: "", remarks: "" });

export default function RateComparison() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [itemName, setItemName] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("Nos");
  const [quotes, setQuotes] = useState([newQuote(), newQuote()]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/rate-comparisons").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line

  useEffect(() => {
    if (!dialog) return;
    api.get("/parties?limit=200").then((r) => setSuppliers(r.data.items.filter((p) => !p.archived))).catch(() => {});
  }, [dialog]);

  const setQuote = (i, k, v) => setQuotes((qs) => qs.map((q, j) => (j === i ? { ...q, [k]: v } : q)));

  const save = async () => {
    if (!itemName.trim()) { toast.error("Item name is required"); return; }
    const valid = quotes.filter((q) => q.supplier_id && +q.rate > 0);
    if (valid.length < 2) { toast.error("Add at least two supplier quotes"); return; }
    setBusy(true);
    try {
      await api.post("/rate-comparisons", {
        item_name: itemName, qty: +qty || 1, unit,
        quotes: valid.map((q) => ({ ...q, rate: +q.rate, gst_pct: +q.gst_pct || 0,
          transport: +q.transport || 0, other: +q.other || 0, delivery_days: +q.delivery_days || 0 })),
      });
      toast.success("Rate comparison saved");
      setDialog(false); setItemName(""); setQty(1); setQuotes([newQuote(), newQuote()]);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const select = async (rc, supplierId) => {
    try {
      await api.post(`/rate-comparisons/${rc.id}/select`, { supplier_id: supplierId });
      toast.success("Supplier selected");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const convert = async (rc) => {
    try {
      const { data: r } = await api.post(`/rate-comparisons/${rc.id}/convert`);
      toast.success(`Draft PO ${r.doc_no} created`);
      nav("/purchases");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="rate-comparison-page">
      <PageHeader title="Rate Comparison" testId="rate-comparison-title" sub="Compare supplier quotes on effective landed rate — convert the winner into a PO">
        <Button data-testid="new-rc-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Comparison</Button>
      </PageHeader>

      {!data ? <TableSkeleton /> : data.items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <EmptyState testId="rc-empty" title="No comparisons yet" body="Add quotes from 2+ suppliers for the same item to find the best effective rate." />
        </div>
      ) : (
        <div className="space-y-4" data-testid="rc-list">
          {data.items.map((rc) => {
            const minRate = Math.min(...rc.quotes.map((q) => q.effective_rate));
            return (
              <div key={rc.id} data-testid={`rc-card-${rc.doc_no}`}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <Scale className="h-4 w-4 text-slate-400" />
                    <div>
                      <p className="font-medium text-sm">{rc.item_name} <span className="text-slate-400 font-normal">× {rc.qty} {rc.unit}</span></p>
                      <p className="text-xs font-mono text-slate-400">{rc.doc_no}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={rc.status.charAt(0).toUpperCase() + rc.status.slice(1)} />
                    {rc.status !== "converted" && (
                      <Button size="sm" data-testid={`rc-convert-${rc.doc_no}`} onClick={() => convert(rc)}
                        className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900 h-8">
                        <ArrowRight className="mr-1 h-3.5 w-3.5" /> Create PO
                      </Button>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="p-3">Supplier</th><th className="p-3 text-right">Rate</th><th className="p-3 text-right">GST %</th>
                      <th className="p-3 text-right">Transport</th><th className="p-3 text-right">Other</th>
                      <th className="p-3 text-right">Effective Rate</th><th className="p-3 text-right">Landed Total</th>
                      <th className="p-3 text-right">Delivery</th><th className="p-3"></th></tr></thead>
                    <tbody>
                      {rc.quotes.map((q) => {
                        const best = q.effective_rate === minRate;
                        const selected = rc.selected_supplier_id === q.supplier_id;
                        return (
                          <tr key={q.supplier_id} data-testid={`rc-quote-${q.supplier_id}`}
                            className={`border-b border-slate-100 dark:border-slate-800 ${best ? "bg-emerald-50/60 dark:bg-emerald-500/5" : ""}`}>
                            <td className="p-3 font-medium">
                              {q.supplier_name}
                              {best && <Badge className="ml-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 border-0 text-[10px]"><Trophy className="mr-0.5 h-2.5 w-2.5" /> LOWEST</Badge>}
                              {selected && <Badge className="ml-1.5 bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400 border-0 text-[10px]">SELECTED</Badge>}
                            </td>
                            <td className="p-3 text-right font-mono"><Money value={q.rate} /></td>
                            <td className="p-3 text-right font-mono text-xs">{q.gst_pct}%</td>
                            <td className="p-3 text-right font-mono text-xs"><Money value={q.transport} /></td>
                            <td className="p-3 text-right font-mono text-xs"><Money value={q.other} /></td>
                            <td className={`p-3 text-right font-mono font-bold ${best ? "text-emerald-600" : ""}`}><Money value={q.effective_rate} /></td>
                            <td className="p-3 text-right font-mono"><Money value={q.landed_total} /></td>
                            <td className="p-3 text-right font-mono text-xs">{q.delivery_days ? `${q.delivery_days}d` : "—"}</td>
                            <td className="p-3 text-right">
                              {rc.status !== "converted" && !selected && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`rc-select-${q.supplier_id}`}
                                  onClick={() => select(rc, q.supplier_id)}>Select</Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="rc-dialog">
          <DialogHeader><DialogTitle className="font-display">New Rate Comparison</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="col-span-2"><Label>Item / Material *</Label><Input data-testid="rc-item" className="mt-1.5" value={itemName} onChange={(e) => setItemName(e.target.value)} placeholder="TMT Steel Bar 12mm" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Qty</Label><Input data-testid="rc-qty" type="number" min="1" className="mt-1.5" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
              <div><Label>Unit</Label><Input className="mt-1.5" value={unit} onChange={(e) => setUnit(e.target.value)} /></div>
            </div>
          </div>
          <div className="space-y-3">
            {quotes.map((q, i) => (
              <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Quote {i + 1}</Label>
                  {quotes.length > 2 && (
                    <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => setQuotes((qs) => qs.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></Button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                  <div className="col-span-2">
                    <Select value={q.supplier_id} onValueChange={(v) => setQuote(i, "supplier_id", v)}>
                      <SelectTrigger data-testid={`rc-supplier-${i}`} className="h-9"><SelectValue placeholder="Supplier" /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input data-testid={`rc-rate-${i}`} type="number" min="0" placeholder="Rate ₹" className="h-9" value={q.rate} onChange={(e) => setQuote(i, "rate", e.target.value)} />
                  <Input type="number" min="0" placeholder="GST %" className="h-9" value={q.gst_pct} onChange={(e) => setQuote(i, "gst_pct", e.target.value)} />
                  <Input type="number" min="0" placeholder="Transport ₹" className="h-9" value={q.transport} onChange={(e) => setQuote(i, "transport", e.target.value)} />
                  <Input type="number" min="0" placeholder="Other ₹" className="h-9" value={q.other} onChange={(e) => setQuote(i, "other", e.target.value)} />
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid="rc-add-quote" onClick={() => setQuotes((qs) => [...qs, newQuote()])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Quote
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="rc-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Compare Quotes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
